import {
  getCategories,
  getCategory,
  getLayouts,
  saveCategory,
  saveCategoryOrder,
} from '@/api/categories';
import { Category, CategoryState, Layout } from '@/types/categories';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

type CategoryGetter = GetterTree<CategoryState, RootState>;

const state: CategoryState = {
  category: null,
  categories: [],
  layouts: [],
};

const getters: CategoryGetter = {
  category: state => state.category,
  categories: state => state.categories,
  layouts: state => state.layouts,
};

const actions: ActionTree<CategoryState, RootState> = {
  async getCategory({ commit }, id: string): Promise<void> {
    commit('setCategory', await getCategory(id));
  },

  async getCategories({ commit }): Promise<void> {
    commit('setCategories', await getCategories());
  },

  async getLayouts({ commit }): Promise<void> {
    commit('setLayouts', await getLayouts());
  },

  async saveCategory({ commit }, category) {
    const item = await saveCategory(category);
    commit('updateCategory', item);
  },

  async saveCategoryOrder({ commit }, update) {
    await saveCategoryOrder(update);
  },
};

const mutations: MutationTree<CategoryState> = {
  newCategory(state: CategoryState) {
    state.category = {
      single_layout_id: '',
      content_page_id: null,
      name: '',
      icon: '',
      strapline: '',
      pagesize: 0,
      banner: {
        id: '',
        colour: null,
        background: null,
        icon: null,
      },
      meta: {
        id: '',
        image_id: null,
        image: null,
        latest_commit: null,
        live_commit: null,
        menu_title: null,
        meta_title: null,
        meta_description: null,
        featured_image_description: null,
        noindex: false,
        canonical_url: null,
        open_graph_title: null,
        open_graph_description: null,
        open_graph_image_crop: null,
        slug: '',
        title: '',
        description: null,
        twitter_title: null,
        twitter_description: null,
        twitter_image_crop: null,
      },
    };
  },

  setCategory(state: CategoryState, category: Category) {
    state.category = category;
  },

  setCategories(categoryState, categoryList: Category[]) {
    categoryState.categories = categoryList;
  },

  setLayouts(categoryState, layoutList: Layout[]) {
    categoryState.layouts = layoutList;
  },

  updateCategory(categoryState, updatedCategory: Category) {
    categoryState.category = updatedCategory;
  },

  setCategoryBannerColour(categoryState, colour: string): void {
    categoryState.category!.banner!.colour = colour;
  },
  setCategoryBannerBackground(categoryState, background: string): void {
    categoryState.category!.banner!.background = background;
  },
  setCategoryBannerIcon(categoryState, icon: string): void {
    categoryState.category!.banner!.icon = icon;
  },

  setCategoryMetaTitle(categoryState: CategoryState, title: string): void {
    categoryState.category!.meta!.meta_title = title;
  },
  setCategoryMetaDescription(
    categoryState: CategoryState,
    description: string,
  ): void {
    categoryState.category!.meta!.meta_description = description;
  },
  setCategoryCanonicalUrl(
    categoryState: CategoryState,
    canonicalUrl: string,
  ): void {
    categoryState.category!.meta!.canonical_url = canonicalUrl;
  },
  setCategoryTwitterTitle(categoryState: CategoryState, title: string): void {
    categoryState.category!.meta!.twitter_title = title;
  },
  setCategoryTwitterDescription(
    categoryState: CategoryState,
    description: string,
  ): void {
    categoryState.category!.meta!.twitter_description = description;
  },
  setCategoryOpenGraphTitle(categoryState: CategoryState, title: string): void {
    categoryState.category!.meta!.open_graph_title = title;
  },
  setCategoryOpenGraphDescription(
    categoryState: CategoryState,
    description: string,
  ): void {
    categoryState.category!.meta!.open_graph_description = description;
  },
};

const categories: Module<CategoryState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default categories;
